import { TEWRequest } from '@cloudflare/cf-edgeworker-http-request-editor'
import { defaults } from '@cloudflare/defaults'
import { getRandomBytes } from './get-random-bytes'
import { TKeyVals } from '@cloudflare/cf-component-key-val-editor'
import { RUNTIME_BASE_URL } from './config'

export interface IFiddleServiceOptions {
  baseUrl?: string
  rawBaseUrl?: string
  maxTimesToTryToFixGCLB?: number
}

export type TPreviewProps = {
  scriptId: string
  sessionId: string
  request: TEWRequest
  updateCount: number
}

export interface IRawRequestResponse {
  body: string
  headers: [string, string][]
  statusText: string
  statusCode: number
}

const RAW_BASE_URL = (() => {
  const url = new URL(RUNTIME_BASE_URL)
  return `${url.protocol}//rawhttp.${url.host}`
})()

export class FiddleService {
  public options: IFiddleServiceOptions
  public static defaults: Partial<IFiddleServiceOptions> = {
    baseUrl: RUNTIME_BASE_URL,
    rawBaseUrl: RAW_BASE_URL,
    maxTimesToTryToFixGCLB: 2,
  }

  constructor(options: IFiddleServiceOptions = {}) {
    this.options = defaults(options, FiddleService.defaults)
  }

  saveContents(body: string) {
    return fetch(`${this.options.baseUrl}/script`, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'text/javascript; charset=UTF-8',
      }),
      credentials: 'same-origin',
      body,
    })
      .then(res => res.json())
      .then(body => body as { id: string })
  }

  fixGCLB(numTimesTried = 0): Promise<boolean> {
    if (numTimesTried > this.options.maxTimesToTryToFixGCLB) {
      throw new RetryLimitError()
    }

    // return Promise.resolve(true)

    return fetch(`${this.options.baseUrl}/fix-gclb`, {
      method: 'POST',
      credentials: 'include',
    })
      .then(res => res.json())
      .then(({ retry }: { retry: boolean }) => {
        if (retry) {
          return this.fixGCLB(numTimesTried + 1)
        }

        return true
      })
  }

  async runRawRequest(preview: TPreviewProps) {
    const { method, body, url, headers: rawHeaders } = preview.request
    const headers = rawHeaders.map(([key, val]) => [`CF-EW-Raw-${key}`, val])
    const { path } = this.parsePreviewUrl(url)
    const credentials = 'same-origin'
    headers.push(['CF-EW-Preview', this.getPreviewId(preview, false)])

    const res = await fetch(`${this.options.rawBaseUrl}/${path}`, {
      method,
      headers,
      credentials,
      ...(!['GET', 'HEAD'].includes(method) ? { body } : {}),
    })

    const resBody = await res.text()
    const resHeaders = Array.from(res.headers.entries())
      .filter(([key]) => key.startsWith('cf-ew-raw-'))
      .map(([key, val]) => [key.replace('cf-ew-raw-', ''), val]) as TKeyVals

    const statusText = res.headers.get('cf-ew-status') || '200 OK'
    const statusCode = +statusText.split(' ')[0]
    resHeaders.unshift(['status', statusText])

    return {
      body: resBody,
      headers: resHeaders,
      statusText,
      statusCode,
    }
  }

  // Can't use `new URL` because we want to handle ill-formed URLs.
  parsePreviewUrl(url: string) {
    const [, protocol = 'http', host, path = ''] = url.match(
      /^(?:(https?):\/\/)?(.*?)(?:\/(.*))?$/,
    )

    return {
      protocol: protocol as 'http' | 'https',
      host,
      path,
    }
  }

  getPreviewId(props: TPreviewProps, fullURL: boolean = true) {
    const {
      scriptId,
      request: { url },
      sessionId,
    } = props

    const { protocol, host, path } = this.parsePreviewUrl(url)

    let result = `${scriptId}${sessionId}${protocol === 'http' ? 0 : 1}${host}`

    if (fullURL) {
      result += `/${path}`
    }

    return result
  }

  getPreviewUrl(props: TPreviewProps) {
    const parsed = new URL(this.options.baseUrl)
    return `${parsed.protocol}//${getRandomBytes()}.${
      parsed.host
    }/.edgeworker-fiddle-init-preview/${this.getPreviewId(props)}`
  }
}

export class RetryLimitError extends Error {
  constructor() {
    super(
      'Request to fix Google Cloud Load Balancer exceeded maximum number of tries',
    )
  }
}



// WEBPACK FOOTER //
// ./src/lib/FiddleService.ts